import { useEffect, useState, useCallback } from "react";
import { motion, useScroll, useSpring, AnimatePresence } from "framer-motion";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { ArrowUp } from "lucide-react";
import { Toaster } from "sonner";
import "@/App.css";
import { OpeningSequence } from "@/components/OpeningSequence";
import { NetworkCanvas } from "@/components/NetworkCanvas";
import { Navbar } from "@/components/Navbar";
import { Hero } from "@/components/Hero";
import { Marquee } from "@/components/Marquee";
import { About } from "@/components/About";
import { TechGraph } from "@/components/TechGraph";
import { Work } from "@/components/Work";
import { Experience } from "@/components/Experience";
import { Contact, Footer } from "@/components/Contact";
import { CaseStudyPage } from "@/pages/CaseStudyPage";
import { useLenis } from "@/hooks/usePortfolio";
import { TID } from "@/constants/testIds";

function ScrollProgress() {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 140, damping: 30, restDelta: 0.001 });
  return (
    <motion.div style={{ scaleX }} className="fixed inset-x-0 top-0 z-[70] h-0.5 origin-left bg-primary" aria-hidden="true" />
  );
}

function BackToTop() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 700);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  if (!show) return null;
  return (
    <button
      data-testid={TID.backToTop}
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      aria-label="Back to top"
      className="fixed bottom-6 right-6 z-50 grid h-11 w-11 place-items-center rounded-full border border-border bg-card/80 text-foreground backdrop-blur-md transition-colors hover:border-primary/50 hover:text-primary"
    >
      <ArrowUp className="h-5 w-5" />
    </button>
  );
}

function ScrollToHash() {
  const { hash, pathname } = useLocation();

  useEffect(() => {
    if (pathname !== "/") return;
    if (!hash) {
      window.scrollTo({ top: 0, behavior: "auto" });
      return;
    }
    const id = hash.replace("#", "");
    const el = document.getElementById(id);
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [hash, pathname]);

  return null;
}

function HomePage() {
  const [intro, setIntro] = useState(() => {
    try {
      return sessionStorage.getItem("introSeen") !== "1";
    } catch {
      return true;
    }
  });

  const finishIntro = useCallback(() => {
    try {
      sessionStorage.setItem("introSeen", "1");
    } catch {
      /* ignore */
    }
    setIntro(false);
  }, []);

  return (
    <>
      <AnimatePresence>{intro && <OpeningSequence onComplete={finishIntro} />}</AnimatePresence>

      <NetworkCanvas />

      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:font-mono focus:text-sm focus:text-primary-foreground"
      >
        Skip to content
      </a>

      <ScrollProgress />
      <Navbar />

      <main id="main" className="relative z-10">
        <Hero />
        <Marquee />
        <About />
        <TechGraph />
        <Work />
        <Experience />
        <Contact />
      </main>

      <Footer />
      <BackToTop />
    </>
  );
}

export default function App() {
  useLenis();

  return (
    <div className="grain relative min-h-screen overflow-x-clip">
      <ScrollToHash />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/work/:projectId" element={<CaseStudyPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Toaster
        position="bottom-center"
        theme="dark"
        toastOptions={{
          style: {
            background: "hsl(222 15% 9%)",
            color: "hsl(210 20% 92%)",
            border: "1px solid hsl(216 14% 16%)",
          },
        }}
      />
    </div>
  );
}
